import type {
  PreparedFeature,
  PreparedPart,
  VectorTileCoordinates,
} from './feature-preparation.js';

export interface TileRegion {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export function parseVectorTileKey(key: string): VectorTileCoordinates {
  const [z, x, y] = key.split('/').map(Number);
  if (
    !/^\d+\/\d+\/\d+$/.test(key) ||
    ![z, x, y].every(Number.isInteger) ||
    z < 0 ||
    x < 0 ||
    y < 0 ||
    x >= 2 ** z ||
    y >= 2 ** z
  ) {
    throw new Error(`Invalid vector tile key: ${key}`);
  }
  return { z, x, y };
}

/** Disjoint parts of a tile not owned by any resident descendant. */
export function vectorTileRegions(
  tile: VectorTileCoordinates,
  descendants: Iterable<VectorTileCoordinates>,
): TileRegion[] {
  let regions: TileRegion[] = [{ minX: 0, minY: 0, maxX: 1, maxY: 1 }];
  for (const child of descendants) {
    if (child.z <= tile.z) continue;
    const scale = 2 ** (child.z - tile.z);
    if (Math.floor(child.x / scale) !== tile.x || Math.floor(child.y / scale) !== tile.y) {
      continue;
    }
    const cut = {
      minX: child.x / scale - tile.x,
      minY: child.y / scale - tile.y,
      maxX: (child.x + 1) / scale - tile.x,
      maxY: (child.y + 1) / scale - tile.y,
    };
    regions = regions.flatMap(region => {
      const minX = Math.max(region.minX, cut.minX);
      const minY = Math.max(region.minY, cut.minY);
      const maxX = Math.min(region.maxX, cut.maxX);
      const maxY = Math.min(region.maxY, cut.maxY);
      if (minX >= maxX || minY >= maxY) return [region];
      return [
        { ...region, maxX: minX },
        { ...region, minX: maxX },
        { minX, maxX, minY: region.minY, maxY: minY },
        { minX, maxX, minY: maxY, maxY: region.maxY },
      ].filter(piece => piece.minX < piece.maxX && piece.minY < piece.maxY);
    });
  }
  return regions;
}

// Tile XY followed by local XYZ and absolute LV95 XY.
type Vertex = [number, number, number, number, number, number, number];

function interpolate(a: Vertex, b: Vertex, t: number): Vertex {
  return [
    a[0] + (b[0] - a[0]) * t,
    a[1] + (b[1] - a[1]) * t,
    a[2] + (b[2] - a[2]) * t,
    a[3] + (b[3] - a[3]) * t,
    a[4] + (b[4] - a[4]) * t,
    a[5] + (b[5] - a[5]) * t,
    a[6] + (b[6] - a[6]) * t,
  ];
}

function contains(region: TileRegion, point: Vertex): boolean {
  return (
    point[0] >= region.minX &&
    point[0] < region.maxX &&
    point[1] >= region.minY &&
    point[1] < region.maxY
  );
}

function snapToRegion(vertex: Vertex, region: TileRegion): Vertex {
  const result: Vertex = [...vertex];
  for (const [axis, edge] of [
    [0, region.minX],
    [0, region.maxX],
    [1, region.minY],
    [1, region.maxY],
  ] as const) {
    if (Math.abs(result[axis] - edge) < 1e-8) result[axis] = edge;
  }
  return result;
}

function clipLine(a: Vertex, b: Vertex, region: TileRegion): Vertex[] {
  let start = 0;
  let end = 1;
  for (const axis of [0, 1] as const) {
    const min = axis === 0 ? region.minX : region.minY;
    const max = axis === 0 ? region.maxX : region.maxY;
    const delta = b[axis] - a[axis];
    if (delta === 0) {
      // Half-open ownership also avoids drawing lines along a shared edge twice.
      if (a[axis] < min || a[axis] >= max) return [];
    } else {
      const one = (min - a[axis]) / delta;
      const two = (max - a[axis]) / delta;
      start = Math.max(start, Math.min(one, two));
      end = Math.min(end, Math.max(one, two));
    }
  }
  return start < end ? [interpolate(a, b, start), interpolate(a, b, end)] : [];
}

function clipTriangle(vertices: Vertex[], region: TileRegion): Vertex[] {
  let result = vertices;
  for (const [axis, edge, sign] of [
    [0, region.minX, 1],
    [0, region.maxX, -1],
    [1, region.minY, 1],
    [1, region.maxY, -1],
  ] as const) {
    const input = result;
    result = [];
    if (!input.length) break;
    let previous = input[input.length - 1];
    let previousInside = (previous[axis] - edge) * sign >= 0;
    for (const current of input) {
      const inside = (current[axis] - edge) * sign >= 0;
      if (inside !== previousInside) {
        result.push(
          interpolate(
            previous,
            current,
            (edge - previous[axis]) / (current[axis] - previous[axis]),
          ),
        );
      }
      if (inside) result.push(current);
      previous = current;
      previousInside = inside;
    }
  }
  return result;
}

function makePart(
  kind: PreparedPart['kind'],
  vertices: Vertex[],
  indices: number[] = [],
): PreparedPart {
  const positions = new Float32Array(vertices.length * 3);
  const worldXY = new Float32Array(vertices.length * 2);
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let i = 0; i < vertices.length; i++) {
    const vertex = vertices[i];
    positions.set(vertex.slice(2, 5), i * 3);
    worldXY.set(vertex.slice(5, 7), i * 2);
    minX = Math.min(minX, vertex[2]);
    minY = Math.min(minY, vertex[3]);
    maxX = Math.max(maxX, vertex[2]);
    maxY = Math.max(maxY, vertex[3]);
  }
  return {
    kind,
    positions,
    worldXY,
    indices: new Uint32Array(indices),
    resolvedZoom: new Int16Array(vertices.length).fill(-1),
    center: [(minX + maxX) / 2, (minY + maxY) / 2],
  };
}

/**
 * Clip primitives, not feature IDs: adjacent MVTs can contain distinct pieces
 * of the same feature. Triangles preserve polygon holes and disconnected fills.
 * New buffers keep the retained source immutable when terrain draping runs.
 */
export function clipVectorTileFeature(
  feature: PreparedFeature,
  regions: TileRegion[],
): PreparedFeature | null {
  if (!regions.length) return null;
  const parts: PreparedPart[] = [];
  for (const part of feature.parts) {
    const tileXY = part.tileXY;
    if (!tileXY || tileXY.length !== part.worldXY.length) {
      throw new Error(`Missing vector tile coordinates for feature ${feature.guid}`);
    }
    const vertices: Vertex[] = [];
    for (let i = 0; i < tileXY.length / 2; i++) {
      vertices.push([
        tileXY[i * 2],
        tileXY[i * 2 + 1],
        part.positions[i * 3],
        part.positions[i * 3 + 1],
        part.positions[i * 3 + 2],
        part.worldXY[i * 2],
        part.worldXY[i * 2 + 1],
      ]);
    }
    for (const region of regions) {
      const regionVertices = vertices.map(vertex => snapToRegion(vertex, region));
      if (regionVertices.every(vertex => contains(region, vertex))) {
        parts.push(makePart(part.kind, regionVertices, [...part.indices]));
        continue;
      }
      if (part.kind === 'point') {
        const points = regionVertices.filter(vertex => contains(region, vertex));
        if (points.length) parts.push(makePart('point', points));
      } else if (part.kind === 'line') {
        let line: Vertex[] = [];
        const flush = (): void => {
          if (line.length) parts.push(makePart('line', line));
          line = [];
        };
        for (let i = 1; i < regionVertices.length; i++) {
          const segment = clipLine(regionVertices[i - 1], regionVertices[i], region);
          if (!segment.length) {
            flush();
            continue;
          }
          const last = line[line.length - 1];
          if (last && last[0] === segment[0][0] && last[1] === segment[0][1]) {
            line.push(segment[1]);
          } else {
            flush();
            line = segment;
          }
        }
        flush();
      } else {
        const triangles: Vertex[] = [];
        const indices: number[] = [];
        for (let i = 0; i < part.indices.length; i += 3) {
          const polygon = clipTriangle(
            [
              regionVertices[part.indices[i]],
              regionVertices[part.indices[i + 1]],
              regionVertices[part.indices[i + 2]],
            ],
            region,
          );
          for (let j = 1; j + 1 < polygon.length; j++) {
            const [a, b, c] = [polygon[0], polygon[j], polygon[j + 1]];
            if ((b[0] - a[0]) * (c[1] - a[1]) === (b[1] - a[1]) * (c[0] - a[0])) continue;
            indices.push(triangles.length, triangles.length + 1, triangles.length + 2);
            triangles.push(a, b, c);
          }
        }
        if (triangles.length) parts.push(makePart('polygon', triangles, indices));
      }
    }
  }
  return parts.length ? { ...feature, parts } : null;
}
